import React from 'react';

import type { EvidenceStatus, IntakeFormData } from '../form/types';
import EvidenceRow from './components/EvidenceRow';

type EvidenceKey = keyof IntakeFormData['evidenceStatuses'];

interface EvidenceGroupProps {
  title: string;
  keys: ReadonlyArray<EvidenceKey>;
  labels: Record<EvidenceKey, string>;
  statuses: IntakeFormData['evidenceStatuses'];
  form: IntakeFormData;
  contextPlaceholderFor: () => string;
  onChange: (key: EvidenceKey, value: EvidenceStatus) => void;
}

/**
 * EvidenceGroup
 *
 * Renders a titled group of evidence rows (e.g., Liability or Treatment).
 * Each row includes a status selector and optional notes input.
 */
const EvidenceGroup: React.FC<EvidenceGroupProps> = ({
  title,
  keys,
  labels,
  statuses,
  form,
  contextPlaceholderFor,
  onChange,
}) => {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
      <div className="border border-gray-200 rounded-md bg-white divide-y">
        {keys.map((key) => {
          let hint: React.ReactNode = null;
          if (key === 'witnesses' && form.incidentInfo.witnesses?.trim()) {
            hint = (
              <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                {form.incidentInfo.witnesses}
              </div>
            );
          } else if (key === 'policeReport' && form.incidentInfo.policeReport) {
            hint = (
              <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                {`${form.incidentInfo.policeDept || 'Police'} report ${
                  form.incidentInfo.policeReportNumber || '(no number)'
                }`}
              </div>
            );
          } else if (key === 'ems' && form.incidentInfo.hospital) {
            hint = (
              <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                {form.incidentInfo.hospital}
              </div>
            );
          } else if (key === 'medicalTreatment') {
            const n = form.medicalWageHistory.treatments?.length || 0;
            if (n > 0) {
              hint = (
                <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                  {`${n} treatment${n > 1 ? 's' : ''} recorded`}
                </div>
              );
            }
          }

          return (
            <EvidenceRow
              key={key}
              id={`review-${key}`}
              title={labels[key]}
              value={statuses[key]}
              onChange={(v) => onChange(key, v)}
              contextPlaceholder={contextPlaceholderFor()}
              hintLines={hint}
            />
          );
        })}
      </div>
    </div>
  );
};

export default EvidenceGroup;
